<template>
    <div class="resolve-thread-dialog">
        <!-- Say what happens to the user's business, not what happens to the
             git branch underneath it. -->
        <p>
            {{
                action === "accept"
                    ? "These changes become part of your business — everyone working on it will see them."
                    : "These changes are thrown away and nothing reaches your business. The discussion is kept, so the reasoning isn't lost."
            }}
        </p>
        <!-- Accept: a "what and why" decision record — What (the change, becomes
             the squash commit subject) + Why (the rationale, a commit trailer). -->
        <template v-if="action === 'accept'">
            <div class="field">
                <label for="resolve-what">What changed</label>
                <Textarea
                    id="resolve-what"
                    v-model="what"
                    placeholder="What did you change? (one line)"
                    rows="2"
                    class="w-full"
                    autofocus
                />
            </div>
            <div class="field">
                <label for="resolve-why">Why</label>
                <Textarea
                    id="resolve-why"
                    v-model="why"
                    placeholder="Why did you change it? (optional)"
                    rows="2"
                    class="w-full"
                />
                <Button
                    label="Suggest a summary"
                    icon="pi pi-sparkles"
                    text
                    size="small"
                    class="suggest-btn"
                    :loading="isSuggesting"
                    data-testid="suggest-message"
                    @click="suggestMessage"
                />
            </div>
        </template>
        <!-- Reject: a single reason is enough — nothing is merged. -->
        <div v-else class="field">
            <label for="resolve-reason">Reason</label>
            <Textarea
                id="resolve-reason"
                v-model="reason"
                placeholder="Why are you discarding these changes?"
                rows="3"
                class="w-full"
                autofocus
            />
        </div>
        <div
            v-if="userChanges.length"
            class="user-changes"
            data-testid="user-changes"
        >
            <h4 class="user-changes-title">
                Changes made directly by users (not the agent)
            </h4>
            <div class="user-change-rows">
                <div
                    v-for="change in userChanges"
                    :key="`${change.entity_type}:${change.entity_id}`"
                    class="user-change-row"
                    data-testid="user-change-row"
                >
                    <span class="user-change-name">
                        {{ change.name || change.entity_id }}
                    </span>
                    <span class="user-change-meta">
                        {{ humanizeType(change.entity_type) }} &middot;
                        {{ change.diff_status }}
                    </span>
                </div>
            </div>
        </div>
        <!-- Behind main: the accept was gated. Surface the pick-a-side resolution
             view; Accept re-enables once every conflict is resolved. -->
        <ConflictResolutionPanel :thread-id="threadId" />
        <p
            v-if="action === 'accept' && hasPendingConflicts"
            class="conflict-hint"
            data-testid="conflict-hint"
        >
            Sort out the clashes above, then apply again.
        </p>
        <div class="dialog-actions">
            <Button
                :label="
                    action === 'accept' ? 'Apply changes' : 'Discard changes'
                "
                :icon="action === 'accept' ? 'pi pi-check' : 'pi pi-times'"
                :severity="action === 'accept' ? 'success' : 'danger'"
                data-testid="resolve-confirm"
                :loading="isResolving"
                :disabled="
                    !canResolve || (action === 'accept' && hasPendingConflicts)
                "
                @click="resolve"
            />
        </div>
    </div>
</template>

<script setup lang="ts">
import { storeToRefs } from "pinia";
import Button from "primevue/button";
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import Textarea from "primevue/textarea";
import { computed, inject, onMounted, ref } from "vue";

import ConflictResolutionPanel from "@/components/Thread/ConflictResolutionPanel.vue";
import { useThreadsStore } from "@/stores/threads-store";

const dialogRef = inject<{ value: DynamicDialogInstance }>("dialogRef");
const store = useThreadsStore();
const { userChanges, hasPendingConflicts } = storeToRefs(store);

const action = dialogRef?.value?.data?.action as "accept" | "reject";
const threadId = dialogRef?.value?.data?.threadId as string;

// Accept uses what + why (the decision record); reject uses a single reason.
const what = ref("");
const why = ref("");
const reason = ref("");
const isResolving = ref(false);
const isSuggesting = ref(false);

// The field that must be filled before resolving: "what" on accept, "reason"
// on reject. "why" is always optional.
const canResolve = computed(() =>
    action === "accept" ? !!what.value.trim() : !!reason.value.trim()
);

// Ask the backend for an LLM-suggested accept message (split into what/why)
// from the thread's discussion + changes, and pre-fill the (editable) fields.
// Never blocks accepting: on failure the store toasts and the user types it.
const suggestMessage = async () => {
    if (!threadId) return;
    isSuggesting.value = true;
    try {
        const suggestion = await store.generateAcceptMessage(threadId);
        if (suggestion) {
            what.value = suggestion.what;
            why.value = suggestion.why;
        }
    } finally {
        isSuggesting.value = false;
    }
};

// Humanise the VFS entity type (`business_entity` -> "domain object").
const TYPE_LABELS: Record<string, string> = {
    business_entity: "domain object",
};
const humanizeType = (entityType: string): string =>
    TYPE_LABELS[entityType] ?? entityType.replace(/_/g, " ");

// Pull the non-agent (user) branch edits when the dialog opens so the reviewer
// sees what they're about to accept/reject alongside the agent's work.
onMounted(() => {
    if (threadId) void store.fetchUserChanges(threadId);
});

const resolve = async () => {
    if (!canResolve.value) return;
    isResolving.value = true;
    try {
        // Accept: "what" is the primary reason, "why" the rationale trailer.
        // Reject: the single reason carries the whole explanation.
        const outcome = await store.resolveThread(
            threadId,
            action,
            action === "accept" ? what.value.trim() : reason.value.trim(),
            action === "accept" ? why.value.trim() : undefined
        );
        // Accept gated by catch-up conflicts: keep the dialog open so the user
        // can resolve them (the panel is now populated); don't close.
        if (outcome === "conflict") return;
        dialogRef?.value?.close();
    } finally {
        // Always clear the local spinner, even if the store unexpectedly rejects.
        isResolving.value = false;
    }
};
</script>

<style scoped>
.resolve-thread-dialog {
    display: flex;
    flex-direction: column;
    gap: 1rem;
}

.field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
}

.field label {
    font-weight: 500;
    font-size: 0.9rem;
}

.dialog-actions {
    display: flex;
    justify-content: flex-end;
}

.conflict-hint {
    margin: 0;
    font-size: 0.82rem;
    color: var(--p-text-secondary-color);
}

.user-changes {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.user-changes-title {
    margin: 0;
    font-size: 0.78rem;
    font-weight: 600;
    color: var(--p-text-secondary-color);
}

.user-change-rows {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}

.user-change-row {
    display: flex;
    flex-direction: column;
    min-width: 0;
    border: 1px solid var(--p-surface-200);
    border-radius: 8px;
    padding: 0.6rem 0.75rem;
}

.user-change-name {
    font-weight: 600;
    font-size: 0.9rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.user-change-meta {
    font-size: 0.72rem;
    color: var(--p-text-muted-color);
    font-family: monospace;
}
</style>
